"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from "next-intl";
import { Ban, Check, RotateCcw, Trash2 } from "lucide-react";
import { DndContext, closestCenter, useDndContext } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import RenderImage from "@/components/atoms/RenderImage";
import { SquareCard } from "@/components/molecules/SquareCard";
import { InputA } from "@/components/atoms/Form/FormInput/InputA";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { MAX_IMAGE_SIZE } from "@/constants/images";
import { useImagesInput } from "./useImagesInput";
export type ImageItem = {
  id: string;
  url: string;
  // Small variant for grid display. `url` remains the identity for save/order.
  thumbnailUrl?: string;
  size?: number;
  file?: File;
  pendingDeletion?: boolean;
};
type Props = {
  value: ImageItem[];
  onChange: (next: ImageItem[]) => void;
  disabled?: boolean;
  canAdd?: boolean;
  showClearAll?: boolean;
  maxImages?: number;
  maxImageSize?: number;
  columns?: 4 | 5;
  className?: string;
};
export function ImagesInput(t0) {
  const $ = _c(52);
  const {
    value,
    onChange,
    disabled,
    canAdd: t1,
    showClearAll,
    maxImages: t2,
    maxImageSize: t3,
    columns: t4,
    className
  } = t0;
  const canAdd = t1 === undefined ? true : t1;
  const maxImages = t2 === undefined ? 20 : t2;
  const maxImageSize = t3 === undefined ? MAX_IMAGE_SIZE : t3;
  const columns = t4 === undefined ? 4 : t4;
  const tCommon = useTranslations("common");
  let t5;
  if ($[0] !== canAdd || $[1] !== disabled || $[2] !== maxImages || $[3] !== onChange || $[4] !== value) {
    t5 = {
      value,
      onChange,
      disabled,
      canAdd,
      maxImages
    };
    $[0] = canAdd;
    $[1] = disabled;
    $[2] = maxImages;
    $[3] = onChange;
    $[4] = value;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    uploadId,
    interactive,
    canUpload,
    usableCount,
    sensors,
    dropHandlers,
    handleDragEnd,
    handleRemove,
    handleRestore,
    handleClearAll,
    addFiles
  } = useImagesInput(t5);
  let t6;
  if ($[6] !== canUpload || $[7] !== className || $[8] !== interactive) {
    t6 = cn("flex flex-col h-full border-2 border-dashed rounded-[32px] p-1 sm:p-4 transition-colors", interactive ? cn("border-primary/40 bg-primary/5", canUpload && "hover:bg-primary/10 cursor-pointer") : "border-border bg-background", className);
    $[6] = canUpload;
    $[7] = className;
    $[8] = interactive;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== columns || $[11] !== handleRemove || $[12] !== handleRestore || $[13] !== interactive || $[14] !== maxImageSize || $[15] !== value) {
    t7 = value.length > 0 && <div className={cn("grid grid-cols-2 auto-rows-min gap-2 sm:gap-3", columns === 5 ? "sm:grid-cols-5" : "sm:grid-cols-4")}><SortableContext items={value.map(_temp)} strategy={rectSortingStrategy}>{value.map(item_0 => <SortableItem key={item_0.id} item={item_0} interactive={interactive} maxImageSize={maxImageSize} onRemove={handleRemove} onRestore={handleRestore} />)}</SortableContext></div>;
    $[10] = columns;
    $[11] = handleRemove;
    $[12] = handleRestore;
    $[13] = interactive;
    $[14] = maxImageSize;
    $[15] = value;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== addFiles || $[18] !== canUpload || $[19] !== maxImages || $[20] !== uploadId || $[21] !== usableCount) {
    t8 = canUpload && <InputA disabled={usableCount >= maxImages} id={uploadId} type="file" accept="image/*" multiple={true} className="hidden" onChange={e => {
      const files = Array.from(e.target.files || []);
      if (files.length) {
        addFiles(files);
      }
    }} />;
    $[17] = addFiles;
    $[18] = canUpload;
    $[19] = maxImages;
    $[20] = uploadId;
    $[21] = usableCount;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== canUpload || $[24] !== maxImageSize || $[25] !== maxImages || $[26] !== tCommon || $[27] !== usableCount || $[28] !== value.length) {
    t9 = canUpload && <div className={cn("flex flex-1 items-center justify-center flex-col w-full p-4 min-h-[160px] text-center", value.length > 0 && "mt-3")}><svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-primary mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg><p className="text-primary font-medium mb-1">Click or drag images here</p><p className="text-muted-foreground text-sm">Supports JPG, PNG, WEBP</p><p className="text-faint-foreground text-xs">{tCommon("maxImageSize", {
          size: Math.round(maxImageSize / 1048576 * 10) / 10
        })}</p>{usableCount > 0 && <p className="mt-3 text-success font-medium">{usableCount} image{usableCount === 1 ? "" : "s"}</p>}{usableCount >= maxImages && <p className="mt-3 text-danger font-medium">Maximum reached</p>}</div>;
    $[23] = canUpload;
    $[24] = maxImageSize;
    $[25] = maxImages;
    $[26] = tCommon;
    $[27] = usableCount;
    $[28] = value.length;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== canUpload || $[31] !== tCommon || $[32] !== value.length) {
    t10 = !canUpload && value.length === 0 && <div className="flex flex-1 items-center justify-center w-full min-h-[160px] text-sm text-faint-foreground">{tCommon("noImages")}</div>;
    $[30] = canUpload;
    $[31] = tCommon;
    $[32] = value.length;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== handleClearAll || $[35] !== interactive || $[36] !== showClearAll || $[37] !== tCommon || $[38] !== usableCount) {
    t11 = showClearAll && interactive && usableCount > 0 && <div className="flex justify-center pt-6 pb-2"><button type="button" onClick={e_0 => {
        e_0.stopPropagation();
        handleClearAll();
      }} className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-danger hover:text-danger/80"><Trash2 className="h-3.5 w-3.5" />{tCommon("clearAll")}</button></div>;
    $[34] = handleClearAll;
    $[35] = interactive;
    $[36] = showClearAll;
    $[37] = tCommon;
    $[38] = usableCount;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== dropHandlers || $[41] !== t10 || $[42] !== t11 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8 || $[46] !== t9) {
    t12 = <div className={t6} {...dropHandlers}>{t7}{t8}{t9}{t10}{t11}</div>;
    $[40] = dropHandlers;
    $[41] = t10;
    $[42] = t11;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== handleDragEnd || $[49] !== sensors || $[50] !== t12) {
    t13 = <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} data-sentry-element="DndContext" data-sentry-component="ImagesInput" data-sentry-source-file="ImagesInput.tsx">{t12}</DndContext>;
    $[48] = handleDragEnd;
    $[49] = sensors;
    $[50] = t12;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
}
function _temp(item) {
  return item.id;
}
function SortableItem(t0) {
  const $ = _c(49);
  const {
    item,
    interactive,
    maxImageSize,
    onRemove,
    onRestore
  } = t0;
  const draggable = interactive && !item.pendingDeletion;
  const t1 = !draggable;
  let t2;
  if ($[0] !== item.id || $[1] !== t1) {
    t2 = {
      id: item.id,
      disabled: t1
    };
    $[0] = item.id;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable(t2);
  const {
    active
  } = useDndContext();
  const anotherDragging = !!active && !isDragging;
  let t3;
  if ($[3] !== transform) {
    t3 = CSS.Transform.toString(transform);
    $[3] = transform;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3 || $[6] !== transition) {
    t4 = {
      transform: t3,
      transition
    };
    $[5] = t3;
    $[6] = transition;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const style = t4;
  const tooLarge = item.size !== undefined && item.size > maxImageSize;
  const t5 = isDragging && "z-50 ring-2 ring-primary rounded-3xl will-change-transform";
  const t6 = anotherDragging && "opacity-40";
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = cn("relative col-span-1 aspect-square", t5, t6);
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== item.size || $[12] !== tooLarge) {
    t8 = tooLarge && <Badge className="z-50 absolute bottom-2 right-2 bg-danger">{`${Math.round((item.size ?? 0) / 1024)} KB`}</Badge>;
    $[11] = item.size;
    $[12] = tooLarge;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== item.id || $[15] !== item.pendingDeletion || $[16] !== onRestore) {
    t9 = item.pendingDeletion ? [{
      icon: <RotateCcw />,
      onClick: e_0 => {
        e_0.stopPropagation();
        onRestore(item.id);
      }
    }] : null;
    $[14] = item.id;
    $[15] = item.pendingDeletion;
    $[16] = onRestore;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== interactive || $[19] !== item.id || $[20] !== item.pendingDeletion || $[21] !== onRemove) {
    t10 = interactive && !item.pendingDeletion ? () => onRemove(item.id) : null;
    $[18] = interactive;
    $[19] = item.id;
    $[20] = item.pendingDeletion;
    $[21] = onRemove;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = draggable && "cursor-grab active:cursor-grabbing";
  let t12;
  if ($[23] !== t11) {
    t12 = cn("w-full h-full", t11);
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== draggable || $[26] !== listeners) {
    t13 = draggable ? listeners : {};
    $[25] = draggable;
    $[26] = listeners;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = item.thumbnailUrl ?? item.url;
  const t15 = item.url;
  const t16 = item.pendingDeletion && "opacity-30";
  let t17;
  if ($[28] !== t16) {
    t17 = cn("object-cover", t16);
    $[28] = t16;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== item.url || $[31] !== t14 || $[32] !== t17) {
    t18 = <RenderImage src={t14} alt={t15} className={t17} data-sentry-element="RenderImage" data-sentry-source-file="ImagesInput.tsx" />;
    $[30] = item.url;
    $[31] = t14;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t12 || $[35] !== t13 || $[36] !== t18) {
    t19 = <div className={t12} {...t13}>{t18}</div>;
    $[34] = t12;
    $[35] = t13;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t10 || $[39] !== t19 || $[40] !== t9) {
    t20 = <SquareCard className="cursor-default" adicionalActions={t9} onDelete={t10} data-sentry-element="SquareCard" data-sentry-source-file="ImagesInput.tsx">{t19}</SquareCard>;
    $[38] = t10;
    $[39] = t19;
    $[40] = t9;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== attributes || $[43] !== setNodeRef || $[44] !== style || $[45] !== t20 || $[46] !== t7 || $[47] !== t8) {
    t21 = <div ref={setNodeRef} style={style} className={t7} onClick={_temp2} {...attributes} data-sentry-component="SortableItem" data-sentry-source-file="ImagesInput.tsx">{t8}{t20}</div>;
    $[42] = attributes;
    $[43] = setNodeRef;
    $[44] = style;
    $[45] = t20;
    $[46] = t7;
    $[47] = t8;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  return t21;
}

// Convenience tokens for the chrome that wraps ImagesInput
function _temp2(e) {
  return e.stopPropagation();
}
export const ImagesInputIcons = {
  Check,
  Ban
};